import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as ReactivateIcon,
} from '@mui/icons-material';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import { ConfirmDialog } from '../ConfirmDialog';
import { TablePagination } from '../shared';
import type { SortConfig } from '../shared';
import { useToast } from '../Toast';
import { getSubjectsDirect, deleteSubjectDirect, reactivateSubjectDirect, hasSubjectTimetableRecords, getClassSubjectAssociations } from '@timetable/api';
import { useAuthStore } from '../../store/authStore';
import type { Subject } from '@timetable/types';
import { queryKeys } from '../../lib/queryKeys';

interface SubjectListProps {
  searchQuery: string;
  sortConfig: SortConfig;
  showInactive?: boolean;
  onEditClick: (subject: Subject) => void;
}

export function SubjectList({ searchQuery, sortConfig, showInactive = false, onEditClick }: SubjectListProps) {
  const queryClient = useQueryClient();
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  const [subjectHasTimetableRecords, setSubjectHasTimetableRecords] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch subjects
  const { data: subjects = [], isLoading, error } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '', includeInactive: showInactive }),
    queryFn: () => getSubjectsDirect({
      school_id: schoolContext || undefined,
      includeInactive: showInactive,
    }),
    enabled: !!schoolContext,
  });

  // Fetch class-subject associations to show badge counts
  const { data: classSubjectAssociations = [] } = useQuery({
    queryKey: queryKeys.classSubjects.list({ school_id: schoolContext || '' }),
    queryFn: () => getClassSubjectAssociations({ school_id: schoolContext || '' }),
    enabled: !!schoolContext,
  });

  const classCountBySubject = useMemo(() => {
    const map = new Map<string, number>();
    for (const assoc of classSubjectAssociations) {
      map.set(assoc.subject_id, (map.get(assoc.subject_id) ?? 0) + 1);
    }
    return map;
  }, [classSubjectAssociations]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSubjectDirect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
      setDeleteDialogOpen(false);
      setSubjectToDelete(null);
      setSubjectHasTimetableRecords(false);
      const msg = subjectHasTimetableRecords ? 'Subject deactivated successfully' : 'Subject deleted successfully';
      showToast(msg, 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete subject', 'error');
      setDeleteDialogOpen(false);
      setSubjectToDelete(null);
      setSubjectHasTimetableRecords(false);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: reactivateSubjectDirect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
      showToast('Subject reactivated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to reactivate subject', 'error');
    },
  });

  // Filter and sort subjects
  const processedSubjects = useMemo(() => {
    let filtered = [...subjects];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (subject) =>
          subject.subject_name.toLowerCase().includes(query) ||
          subject.subject_code?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string = '';
      let bValue: string = '';

      switch (sortConfig.field) {
        case 'subject_name':
          aValue = a.subject_name.toLowerCase();
          bValue = b.subject_name.toLowerCase();
          break;
        case 'subject_code':
          aValue = (a.subject_code || '').toLowerCase();
          bValue = (b.subject_code || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [subjects, searchQuery, sortConfig]);

  // Paginate
  const paginatedSubjects = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedSubjects.slice(startIndex, startIndex + rowsPerPage);
  }, [processedSubjects, page, rowsPerPage]);

  const handleDeleteClick = async (subject: Subject) => {
    setSubjectToDelete(subject);
    const hasTimetableRecords = await hasSubjectTimetableRecords(subject.id);
    setSubjectHasTimetableRecords(hasTimetableRecords);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (subjectToDelete) {
      deleteMutation.mutate(subjectToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSubjectToDelete(null);
    setSubjectHasTimetableRecords(false);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load subjects: {(error as Error).message}
      </Alert>
    );
  }

  if (processedSubjects.length === 0) {
    return (
      <EmptyState
        title="No subjects found"
        description={
          searchQuery
            ? 'Try adjusting your search query'
            : 'Get started by adding your first subject'
        }
      />
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Subject Name</TableCell>
              <TableCell>Subject Code</TableCell>
              <TableCell>Color</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSubjects.map((subject) => (
              <TableRow key={subject.id} hover sx={!subject.is_active ? { opacity: 0.5 } : undefined}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{subject.subject_name}</Typography>
                    {(() => {
                      const count = classCountBySubject.get(subject.id) ?? 0;
                      return count === 0 ? (
                        <Chip label="All classes" size="small" variant="outlined" />
                      ) : (
                        <Chip label={`${count} classes`} size="small" variant="outlined" color="primary" />
                      );
                    })()}
                  </Box>
                </TableCell>
                <TableCell>
                  {subject.subject_code ? (
                    <Chip label={subject.subject_code} size="small" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {subject.color_code ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: subject.color_code,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {subject.color_code}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No color
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {subject.is_active === false ? (
                    <Tooltip title="Reactivate subject">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => reactivateMutation.mutate(subject.id)}
                        title="Reactivate subject"
                      >
                        <ReactivateIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => onEditClick(subject)}
                        title="Edit subject"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(subject)}
                        title="Delete subject"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          count={processedSubjects.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={subjectHasTimetableRecords ? 'Deactivate Subject' : 'Delete Subject'}
        message={
          subjectToDelete
            ? subjectHasTimetableRecords
              ? `"${subjectToDelete.subject_name}" has existing timetable records and will be deactivated instead of deleted. Historical data will be preserved.`
              : `Are you sure you want to delete ${subjectToDelete.subject_name}? This action cannot be undone.`
            : ''
        }
        confirmLabel={subjectHasTimetableRecords ? 'Deactivate' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </>
  );
}
